import { useMemo, useState } from "react";
import { formatDate } from "../../utils/dateUtils";

const MilestoneTable = ({ data }) => {
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(5);
  const [sortDir, setSortDir] = useState("desc");
  const [filterCategory, setFilterCategory] = useState("all");

  const filteredData = useMemo(() => {
    if (filterCategory === "all") return data;
    return data.filter(item => item.category === filterCategory);
  }, [data, filterCategory]);

  const sorted = useMemo(() => {
    return filteredData.slice().sort((a, b) => {
      if (a.date < b.date) return sortDir === "asc" ? -1 : 1;
      if (a.date > b.date) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  }, [filteredData, sortDir]);

  const pageCount = Math.ceil(sorted.length / rowsPerPage);
  const pageData = sorted.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  const toggleSort = () => {
    setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    setPage(1);
  };

  const getCategoryIcon = (category) => {
    const icons = {
      physical: "🏃",
      cognitive: "🧠",
      social: "👥",
      language: "🗣️",
      other: "✨"
    };
    return icons[category] || "📝";
  };

  const getCategoryBadge = (category) => {
    const badges = {
      physical: "badge bg-success",
      cognitive: "badge bg-info",
      social: "badge bg-warning text-dark",
      language: "badge bg-primary",
      other: "badge bg-secondary"
    };
    return badges[category] || "badge bg-secondary";
  };

  return (
    <>
      <div className="mb-3">
        <select
          className="form-select form-select-sm"
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          style={{ width: "200px" }}
        >
          <option value="all">All Milestones</option>
          <option value="physical">Physical</option>
          <option value="cognitive">Cognitive</option>
          <option value="social">Social</option>
          <option value="language">Language</option>
          <option value="other">Other</option>
        </select>
      </div>

      <div className="table-responsive">
        <table className="table table-sm table-striped table-hover">
          <thead className="table-light">
            <tr>
              <th scope="col" style={{ cursor: "pointer" }} onClick={toggleSort}>
                DateTime {sortDir === "desc" ? "▼" : "▲"}
              </th>
              <th scope="col">Category</th>
              <th scope="col">Milestone</th>
              <th scope="col">Notes</th>
            </tr>
          </thead>
          <tbody>
            {pageData.map((row, index) => (
              <tr key={index}>
                <td>{formatDate(row.date)}</td>
                <td>
                  <span className={`badge ${getCategoryBadge(row.category)}`}>
                    {getCategoryIcon(row.category)} {row.category}
                  </span>
                </td>
                <td>
                  <strong>{row.milestone}</strong>
                  {row.photoURL && (
                    <a href={row.photoURL} target="_blank" rel="noreferrer" style={{ marginLeft: "0.5rem" }}>
                      <img
                        src={row.photoURL}
                        alt="milestone"
                        style={{ width: "36px", height: "36px", objectFit: "cover", borderRadius: "6px", border: "2px solid var(--border-light)", verticalAlign: "middle" }}
                      />
                    </a>
                  )}
                </td>
                <td>{row.notes || <span className="text-muted">-</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pageCount > 1 && (
        <nav aria-label="Page navigation">
          <ul className="pagination pagination-sm">
            <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
              <button
                className="page-link"
                onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                disabled={page === 1}
              >
                Previous
              </button>
            </li>
            {[...Array(pageCount)].map((_, idx) => {
              const num = idx + 1;
              return (
                <li key={num} className={`page-item ${num === page ? "active" : ""}`}>
                  <button className="page-link" onClick={() => setPage(num)}>
                    {num}
                  </button>
                </li>
              );
            })}
            <li className={`page-item ${page === pageCount ? "disabled" : ""}`}>
              <button
                className="page-link"
                onClick={() => setPage((prev) => Math.min(prev + 1, pageCount))}
                disabled={page === pageCount}
              >
                Next
              </button>
            </li>
          </ul>
        </nav>
      )}
    </>
  );
};

export default MilestoneTable;
